import { FormEvent, useEffect, useState } from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import {
  EventType,
  getStandAlonePageName,
  logAnalyticsEvent,
  PageTags,
} from '@pass-frontend/common/analytics';
import { ContentWidthWrapper } from '@pass-frontend/common/components/content-width-wrapper/ui';
import { STANDALONE_PATHS } from '@pass-frontend/common/constants/paths';
import { useIntl } from '@pass-frontend/common/i18n';
import { useEligibilityCheck } from '@pass-frontend/features/standalone/data-bridge';
import { MEDIA_QUERY } from '@pass-frontend/utility/style-helpers';
import { NotEligibleModal } from '../not-eligible-modal/not-eligible-modal';
import { YouAreEligibleModal } from '../you-are-eligible-modal/you-are-eligible-modal';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ZipCodeCheckProps {}

const StyledInput = styled.div.attrs({
  className: 'is-display-flex is-flex-direction-column',
})`
  margin-bottom: 24px;

  label {
    margin-bottom: 4px;
  }

  input {
    border: 1px solid #b3b3b3;
    border-radius: 32px;
    height: 48px;
    padding: 14px 16px;
  }

  ${MEDIA_QUERY.desktop} {
    label {
      margin-bottom: 8px;
    }
  }
`;

const StyledZipCodeCheck = styled.div`
  margin-top: 48px;

  h1 {
    margin-bottom: 32px;
  }

  .btn {
    width: 160px;
    font-size: 16px;
    line-height: 20px;
    padding: 10px 0;
  }

  .disabled {
    pointer-events: none;
    background-color: #b1b2b4;
  }

  ${MEDIA_QUERY.desktop} {
    width: 616px;
    margin: 48px auto auto;

    h1 {
      margin-bottom: 24px;
    }
  }

  ${MEDIA_QUERY.mobileAndTablet} {
    .btn-primary {
      width: 100%;
    }

    margin-top: 32px;
  }
`;

export function ZipCodeCheckForm(props: ZipCodeCheckProps) {
  const { t } = useIntl();
  const {
    fetchMemberByEligibility,
    memberCheckInProgress,
    registrationUrl,
    serviceSector,
    memberCode,
    isNotEligibleModalOpen,
    isEligibleModalOpen,
    onConfirmModalClose,
    memberBigNFromUI,
  } = useEligibilityCheck();

  const [zipCode, setZipCode] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const { firstName, lastName, birthDate } = memberBigNFromUI;

  const onEligibilitySubmit = (event: FormEvent) => {
    event.preventDefault();
    if (firstName && lastName && birthDate && zipCode) {
      fetchMemberByEligibility(
        {
          firstName: firstName,
          lastName: lastName,
          birthDate: birthDate,
          zipCode: zipCode,
        },
        '',
        false,
      );

      logAnalyticsEvent(
        EventType.FormSubmit,
        {
          type: EventType.FormSubmit,
          [PageTags.TriggerUIElementName]: 'Submit',
        },
        event.nativeEvent,
      );
    }
  };

  const logAdobeEventOnModalCancel = () => {
    // eslint-disable-next-line no-restricted-globals
    event?.stopPropagation();
    logAnalyticsEvent(
      EventType.PageNav,
      {
        pageName: getStandAlonePageName(STANDALONE_PATHS.ZIP_CODE_CHECK),
      },
      // eslint-disable-next-line no-restricted-globals
      event,
    );
  };

  useEffect(() => {
    memberCheckInProgress
      ? setIsSubmitDisabled(true)
      : setIsSubmitDisabled(false);
  }, [memberCheckInProgress]);

  useEffect(() => {
    zipCode ? setIsSubmitDisabled(false) : setIsSubmitDisabled(true);
  }, [zipCode]);

  return (
    <ContentWidthWrapper>
      <StyledZipCodeCheck>
        <h1 className="is-h1 has-text-blue-1">
          {t('eligibilityCheck.form.zipCodeCheck.title')}
        </h1>
        <form onSubmit={(e: FormEvent) => onEligibilitySubmit(e)}>
          <StyledInput>
            <label className="is-body-2-regular" htmlFor="zip-code">
              {t('eligibilityCheck.form.zipCode')}
            </label>
            <input
              type="text"
              id="zip-code"
              name="zipCode"
              onChange={(e) => setZipCode(e.target.value)}
            />
          </StyledInput>

          <input
            type="submit"
            value={
              memberCheckInProgress
                ? t('eligibilityCheck.submittingButtonText')
                : t('eligibilityCheck.submitButtonText')
            }
            disabled={isSubmitDisabled || memberCheckInProgress}
            className={cx('btn btn-primary', {
              disabled: isSubmitDisabled || memberCheckInProgress,
              loading: memberCheckInProgress,
            })}
            data-ui-element-name="zip-code-check-form-submit-btn"
          />

          <YouAreEligibleModal
            isOpen={isEligibleModalOpen}
            onRequestedClose={() => {
              onConfirmModalClose();
              logAdobeEventOnModalCancel();
            }}
            registrationUrl={registrationUrl}
            data-ui-element-name="you-are-eligible-modal"
            aria-modal={true}
            serviceSector={serviceSector}
            memberCode={memberCode}
          />

          <NotEligibleModal
            isOpen={isNotEligibleModalOpen}
            onRequestedClose={() => {
              onConfirmModalClose();
              logAdobeEventOnModalCancel();
            }}
            data-ui-element-name="we-cant-find-you-modal"
            aria-modal={true}
          />
        </form>
      </StyledZipCodeCheck>
    </ContentWidthWrapper>
  );
}

export default ZipCodeCheckForm;
